import { Button, FormMessage, LayoutContainer, Loader, RteWrapper } from '@greystar/designsystem';
import { getLinkIcon, useAnalytics, formatPhoneNumber } from '@greystar/lib';
import { Field, RichText as JssRichText } from '@sitecore-jss/sitecore-jss-nextjs';
import { FormEvent, useCallback, useEffect, useRef, useState, JSX } from 'react';
import { FormProps, FormRow as FormRowProps } from '../../../component-props';
import { FormRow } from '../fields/FormRow/FormRow';
import { buildField } from './FormFieldFactory';
import { FormCallbacks, FormFieldRefs, FormSubmitStatus } from './FormTypes';

export const Form = ({
  props,
  callbacks,
}: {
  props: FormProps;
  callbacks?: FormCallbacks;
}): JSX.Element => {
  const formRef = useRef<HTMLFormElement>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const fieldRefs = useRef<FormFieldRefs>({});
  const [formSilent, setFormSilent] = useState<boolean>(true);
  const [submitStatus, setSubmitStatus] = useState<FormSubmitStatus | null>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const analytics = useAnalytics();
  const [hasStartedFilling, setHasStartedFilling] = useState<boolean>(false);
  const trackFormEvent = useCallback(
    async (
      eventType: 'start' | 'submit' | 'error' | 'call_click',
      additionalData: Record<string, string> = {},
      formData?: FormData
    ) => {
      const baseEventData = {
        state: props.data?.propertyLocation?.state ?? '',
        property_location: props.data?.propertyLocation?.address ?? '',
        property_id: props.data?.propertyId ?? '',
        property_name: props.data?.propertyName ?? '',
        module_name: 'contact us',
        inpage_location: 'middle',
        click_type: 'internal',
        destination_url: '',
      };

      const email = (formData?.get('email') as string) ?? '';
      const phone = formatPhoneNumber(
        (formData?.get('phone') as string) ?? (formData?.get('phoneNumber') as string) ?? ''
      );

      const eventMap = {
        start: {
          event: 'property_contact_us_start',
          action_type: 'start',
          click_text: 'Form interaction started',
        },
        submit: {
          event: 'property_contact_us_submit',
          action_type: 'submit',
          click_text: 'Form submitted',
          user_data: {
            email: email,
            phone_number: phone,
          },
        },
        error: {
          event: 'property_contact_us_error',
          action_type: 'submit',
          click_text: additionalData.errorMessage ?? 'Form submission error',
          user_data: {
            email: email,
            phone_number: phone,
          },
        },
        call_click: {
          event: 'property_call_click',
          action_type: 'call click',
          click_text: additionalData.phoneNumber ?? 'Call button clicked',
        },
      };

      analytics.trackEvent(eventMap[eventType].event, {
        ...baseEventData,
        ...eventMap[eventType],
        ...additionalData,
      });
    },
    [analytics, props.data]
  );

  useEffect(() => {
    const handleFieldInteraction = () => {
      if (!hasStartedFilling) {
        setHasStartedFilling(true);
        trackFormEvent('start').catch(console.error);
      }
    };

    const form = formRef.current;
    if (form) {
      form.addEventListener('focusin', handleFieldInteraction);
      return () => form.removeEventListener('focusin', handleFieldInteraction);
    }
    return undefined;
  }, [hasStartedFilling, trackFormEvent]);

  const isFormValid = () => {
    return Object.values(fieldRefs.current).reduce(
      (valid, field) => (field.isValid?.() ?? true) && valid,
      true
    );
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isFormValid() || !formRef.current) {
      setFormSilent(false);
      return;
    }
    setIsLoading(true);
    const formData = new FormData(formRef.current);
    callbacks?.onSubmit?.(formData);
    if (callbacks?.action) {
      const result = await callbacks?.action(formData);
      if (result.success) {
        setSubmitStatus('success');
        callbacks?.onSuccess?.(formData, result);
        trackFormEvent('submit', {}, formData).catch(console.error);
      } else {
        setSubmitStatus('error');
        trackFormEvent('error', {}, formData).catch(console.error);
        callbacks?.onError?.(formData, result);
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const handleCallClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      const phoneLink = target.closest('a[href^="tel:"]');
      if (!phoneLink) return;

      const contactSection = document.getElementById('contact-us');
      if (!contactSection?.contains(phoneLink)) return;

      event.preventDefault();
      const click_text = phoneLink.getAttribute('href')?.replace('tel:', '') ?? '';
      trackFormEvent('call_click', { click_text }).catch(console.error);

      setTimeout(() => {
        window.location.href = `tel:${click_text}`;
      }, 100);
    };

    document.addEventListener('click', handleCallClick);
    return () => document.removeEventListener('click', handleCallClick);
  }, [trackFormEvent]);

  useEffect(() => {
    if (submitStatus != null && messageRef.current?.scrollIntoView) {
      messageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [submitStatus]);

  const fields = props.fields.formFields
    .map((field) => {
      const fieldType = field.fields.fieldType as Field<string>;
      if (fieldType?.value === 'row') {
        return (
          <FormRow
            key={field.id}
            fieldRefs={fieldRefs.current}
            formSilent={formSilent}
            {...(field as FormRowProps)}
          />
        );
      } else {
        return buildField({
          field,
          fieldRefs: fieldRefs.current,
          formSilent,
        });
      }
    })
    .filter((field) => field);

  return (
    <>
      {submitStatus !== 'success' && (
        <form ref={formRef} onSubmit={submit} data-testid="form" autoComplete="off">
          <LayoutContainer gap="lg" flexDirection="column" aria-live="polite" id="form-layout">
            {fields}
            {submitStatus == 'error' && (
              <div ref={messageRef}>
                <FormMessage
                  text="We’re sorry, something went wrong."
                  text2="Please try again later."
                  variant="error"
                />
              </div>
            )}
            {isLoading && <Loader />}
            {!isLoading && (
              <Button
                icon={getLinkIcon()}
                label="Submit"
                variant="primary"
                style={{ width: 'fit-content' }}
              />
            )}
            {props.fields.formFooter.value && (
              <LayoutContainer maxWidth="lg">
                <RteWrapper>
                  <div className="[&_p]:mb-0">
                    <JssRichText field={props.fields.formFooter} />
                  </div>
                </RteWrapper>
              </LayoutContainer>
            )}
          </LayoutContainer>
        </form>
      )}
      {submitStatus === 'success' && (
        <div ref={messageRef}>
          <FormMessage
            text="Thank you! Your message has been sent."
            text2="We will get back to you as soon as possible."
            variant="success"
          />
        </div>
      )}
    </>
  );
};

export default Form;
